/**
 * Company Profile page (CR-6247).
 *
 * The click-through target for a company in Admin Setup > Organisation
 * Structure, giving a company the same structured profile a supplier has
 * instead of the edit pop-up being the only view of its data.
 *
 * Tabs: Company Overview, Users, Approvals, Settings, Group Structure. Editing
 * stays in the Organisation edit pop-up — this page is the read surface, apart
 * from the Settings tab's regional settings.
 *
 * CR-6250: the same page doubles as the Customer Profile a supplier opens from
 * its Customers page. Suppliers get Overview / Users / Approvals only — no
 * Settings (explicitly barred by the ticket) and no Group Structure, which
 * would expose the customer's internal org hierarchy.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Loader2, AlertCircle, Mail, Power } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ApprovalsTab } from './supplier-detail';
import { useIsSupplier } from '../hooks';
import {
  OverviewTab,
  UsersTab,
  SettingsTab,
  GroupStructureTab,
  useCompanyProfile,
} from './company-profile';

export default function CompanyProfile() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  // CR-6250: a supplier reaches this page from Customers, not Admin Setup, and
  // sees a reduced tab set.
  const isSupplierView = useIsSupplier();
  const backTo = isSupplierView ? '/customers' : '/admin/setup';
  const backLabel = isSupplierView ? 'Back to Customers' : 'Back to Admin Setup';
  const {
    companyId,
    company,
    loading,
    error,
    users,
    filteredUsers,
    usersLoading,
    usersError,
    userSearch,
    setUserSearch,
    refreshCompany,
  } = useCompanyProfile();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(backTo)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {backLabel}
        </Button>
        <Card>
          <CardContent className="py-16">
            <div className="flex flex-col items-center gap-3 text-center">
              <AlertCircle className="h-10 w-10 text-destructive" />
              <p className="text-sm text-destructive">
                {error || 'This company could not be found.'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate(backTo)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        {backLabel}
      </Button>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Building2 className="h-7 w-7 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold">{company.name}</h1>
              <Badge
                variant="outline"
                className={
                  company.is_active
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-red-200 bg-red-50 text-red-700'
                }
              >
                <Power className="h-3 w-3 mr-1" />
                {company.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {isSupplierView ? 'Customer Profile' : 'Company Profile'}
              {company.group_name ? ` — ${company.group_name}` : ''}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            if (company.primary_email) {
              window.open(`mailto:${company.primary_email}`, '_blank');
            } else {
              toast.error('No contact email available for this company');
            }
          }}
        >
          <Mail className="h-4 w-4 mr-2" />
          Contact
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <TabsTrigger value="overview">Company Overview</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="approvals">Approvals</TabsTrigger>
          {/* CR-6250: Settings is explicitly barred for suppliers, and Group
              Structure would expose the customer's internal hierarchy. */}
          {!isSupplierView && <TabsTrigger value="settings">Settings</TabsTrigger>}
          {!isSupplierView && (
            <TabsTrigger value="group-structure">Group Structure</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab company={company} hideCommercialFields={isSupplierView} />
        </TabsContent>

        <TabsContent value="users">
          <UsersTab
            users={users}
            filteredUsers={filteredUsers}
            loading={usersLoading}
            error={usersError}
            search={userSearch}
            onSearchChange={setUserSearch}
            companyName={company.name}
            hideManageUsers={isSupplierView}
          />
        </TabsContent>

        <TabsContent value="approvals">
          {/* Same Approval Register source the supplier profile reads (CR-5861). */}
          <ApprovalsTab companyId={companyId} subjectLabel="company" />
        </TabsContent>

        {!isSupplierView && (
          <TabsContent value="settings">
            <SettingsTab company={company} onCompanyRefresh={refreshCompany} />
          </TabsContent>
        )}

        {!isSupplierView && (
          <TabsContent value="group-structure">
            <GroupStructureTab company={company} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
